import { Timestamp } from "firebase/firestore";

/**
 * Webhook Event Types
 */
export enum WebhookEventType {
  CLAIM_SUBMITTED = "claim_submitted",
  CLAIM_APPROVED = "claim_approved",
  CLAIM_REJECTED = "claim_rejected",
  OWNER_INACTIVE = "owner_inactive",
  TRIAL_EXPIRING_SOON = "trial_expiring_soon",
  TRIAL_EXPIRED = "trial_expired",
  TRIAL_EXTENDED = "trial_extended",
  SUBSCRIPTION_ACTIVATED = "subscription_activated",
  PAYMENT_FAILED = "payment_failed",
  OWNER_WELCOME = "owner_welcome",
  BAR_VERIFIED = "bar_verified",
  CO_OWNER_INVITED = "co_owner_invited",
  MONTHLY_BAR_STATS = "monthly_bar_stats",
}

/**
 * Single Webhook Configuration
 */
export interface WebhookConfig {
  url: string;
  enabled: boolean;
  description?: string;
}

/**
 * Complete Webhooks Configuration Document
 * Stored in Firestore: config/webhooks
 */
export interface WebhooksConfiguration {
  claimSubmitted: WebhookConfig;
  claimApproved: WebhookConfig;
  claimRejected: WebhookConfig;
  ownerInactive: WebhookConfig;
  trialExpiringSoon: WebhookConfig;
  trialExpired: WebhookConfig;
  trialExtended: WebhookConfig;
  subscriptionActivated: WebhookConfig;
  paymentFailed: WebhookConfig;
  ownerWelcome: WebhookConfig;
  barVerified: WebhookConfig;
  coOwnerInvited: WebhookConfig;
  monthlyBarStats: WebhookConfig;
  updatedAt?: Timestamp;
  updatedBy?: string;
}

/**
 * Webhook Payload for Claim Submitted Event
 */
export interface ClaimSubmittedPayload {
  eventType: WebhookEventType.CLAIM_SUBMITTED;
  timestamp: string;
  claimId: string;
  barId: string;
  barName: string;
  userId: string;
  userEmail: string;
  userName?: string;
  message?: string;
  proofImages?: string[];
}

/**
 * Webhook Payload for Claim Approved Event
 */
export interface ClaimApprovedPayload {
  eventType: WebhookEventType.CLAIM_APPROVED;
  timestamp: string;
  claimId: string;
  barId: string;
  barName: string;
  ownerId: string;
  ownerEmail: string;
  ownerName?: string;
  approvedBy: string;
  approvedAt: string;
  trialEndsAt?: string;
}

/**
 * Webhook Payload for Claim Rejected Event
 */
export interface ClaimRejectedPayload {
  eventType: WebhookEventType.CLAIM_REJECTED;
  timestamp: string;
  claimId: string;
  barId: string;
  barName: string;
  userId: string;
  userEmail: string;
  userName?: string;
  rejectedBy: string;
  rejectedAt: string;
  rejectionReason?: string;
}

/**
 * Webhook Payload for Owner Inactive Event
 */
export interface OwnerInactivePayload {
  eventType: WebhookEventType.OWNER_INACTIVE;
  timestamp: string;
  ownerId: string;
  ownerEmail: string;
  ownerName?: string;
  barId: string;
  barName: string;
  lastActivityAt?: string;
  inactiveDays: number;
}

/**
 * Webhook Payload for Trial Expiring Soon Event
 */
export interface TrialExpiringSoonPayload {
  eventType: WebhookEventType.TRIAL_EXPIRING_SOON;
  timestamp: string;
  ownerId: string;
  ownerEmail: string;
  ownerName?: string;
  barId: string;
  barName: string;
  trialEndsAt: string;
  daysRemaining: number;
}

/**
 * Webhook Payload for Trial Expired Event
 */
export interface TrialExpiredPayload {
  eventType: WebhookEventType.TRIAL_EXPIRED;
  timestamp: string;
  ownerId: string;
  ownerEmail: string;
  ownerName?: string;
  barId: string;
  barName: string;
  trialEndedAt: string;
}

/**
 * Webhook Payload for Trial Extended Event
 */
export interface TrialExtendedPayload {
  eventType: WebhookEventType.TRIAL_EXTENDED;
  timestamp: string;
  ownerId: string;
  ownerEmail: string;
  ownerName?: string;
  barId: string;
  barName: string;
  newTrialEndsAt: string;
  previousTrialEndsAt?: string;
  extendedByDays: number;
  extendedByAdminId: string;
}

export interface SubscriptionActivatedPayload {
  eventType: WebhookEventType.SUBSCRIPTION_ACTIVATED;
  timestamp: string;
  ownerId: string;
  ownerEmail: string;
  ownerName?: string;
  barId: string;
  barName: string;
  activatedAt: string;
  amountPaid?: number;
  currency?: string;
}

export interface PaymentFailedPayload {
  eventType: WebhookEventType.PAYMENT_FAILED;
  timestamp: string;
  ownerId: string;
  ownerEmail: string;
  ownerName?: string;
  barId: string;
  barName: string;
  gracePeriodEndsAt?: string;
}

export interface OwnerWelcomePayload {
  eventType: WebhookEventType.OWNER_WELCOME;
  timestamp: string;
  ownerId: string;
  ownerEmail: string;
  ownerName?: string;
  barId: string;
  barName: string;
  trialEndsAt?: string;
}

export interface BarVerifiedPayload {
  eventType: WebhookEventType.BAR_VERIFIED;
  timestamp: string;
  ownerId: string;
  ownerEmail: string;
  ownerName?: string;
  barId: string;
  barName: string;
  verifiedAt: string;
}

export interface CoOwnerInvitedPayload {
  eventType: WebhookEventType.CO_OWNER_INVITED;
  timestamp: string;
  invitedByOwnerId: string;
  invitedByOwnerEmail: string;
  invitedByOwnerName?: string;
  invitedEmail?: string;
  barId: string;
  barName: string;
  invitationToken: string;
  expiresAt?: string;
}

export interface MonthlyBarStatsPayload {
  eventType: WebhookEventType.MONTHLY_BAR_STATS;
  timestamp: string;
  ownerId: string;
  ownerEmail: string;
  ownerName?: string;
  barId: string;
  barName: string;
  month: string;
  followerCount: number;
  checkInCount?: number;
  viewCount?: number;
}

/**
 * Union type for all webhook payloads
 */
export type WebhookPayload =
  | ClaimSubmittedPayload
  | ClaimApprovedPayload
  | ClaimRejectedPayload
  | OwnerInactivePayload
  | TrialExpiringSoonPayload
  | TrialExpiredPayload
  | TrialExtendedPayload
  | SubscriptionActivatedPayload
  | PaymentFailedPayload
  | OwnerWelcomePayload
  | BarVerifiedPayload
  | CoOwnerInvitedPayload
  | MonthlyBarStatsPayload;

/**
 * Webhook Test Result
 */
export interface WebhookTestResult {
  success: boolean;
  statusCode?: number;
  message: string;
  responseTime?: number;
  error?: string;
}
